import React from 'react';
import { FileText, Shield, FlaskConical, FileSignature, Banknote, Ship, CheckCircle2 } from 'lucide-react';

/**
 * Procurement procedure for market-priced commodity products (Oil & Gas, Real
 * Estate, bulk metals, etc.). Shown on the product detail page next to the
 * Request-Quote CTA so buyers understand the B2B workflow before submitting.
 */
const STEPS = [
  {
    title: 'Submit LOI / RFQ',
    icon: FileText,
    body: 'Buyer submits a Letter of Intent or Request-for-Quote with target volume, destination port, and target delivery window.',
  },
  {
    title: 'Receive SCO + Spec Sheet',
    icon: FileSignature,
    body: 'Seller issues a Soft Corporate Offer with current price, product specification, payment terms, and delivery schedule.',
  },
  {
    title: 'ICPO + Banking Docs',
    icon: Shield,
    body: 'Buyer returns an Irrevocable Corporate Purchase Order plus Bank Comfort Letter (or proof of funds) to lock the deal.',
  },
  {
    title: 'Sales & Purchase Agreement',
    icon: FileSignature,
    body: 'Both parties execute the SPA. Contract is lodged at the loading terminal; product is reserved against the buyer.',
  },
  {
    title: 'SGS / Independent Inspection',
    icon: FlaskConical,
    body: 'Pre-shipment inspection by SGS, Intertek, or Saybolt confirms quantity and conformity to spec at loading port.',
  },
  {
    title: 'Payment via SBLC / MT103 / DLC',
    icon: Banknote,
    body: 'Buyer issues SBLC, MT103, or DLC against shipping documents. Funds release upon Bill of Lading or SGS clearance.',
  },
  {
    title: 'Vessel Loading & Delivery',
    icon: Ship,
    body: 'Cargo lifted (FOB / CIF / TTO / TTT depending on Incoterm). Buyer takes title at the agreed delivery point.',
  },
];

export default function ProcurementProcedure() {
  return (
    <section
      className="mt-10 bg-muted/30 rounded-2xl border border-border/40 p-6 md:p-8"
      data-testid="procurement-procedure"
    >
      <div className="mb-6">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 block">
          Procurement Procedure
        </span>
        <h2 className="text-xl md:text-2xl font-heading font-medium text-foreground">
          How bulk commodity orders are placed
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Christ Kingdom Marketplace follows standard international trade procedure for
          oil, gas, and bulk commodities. Submit your <strong>LOI / RFQ</strong> via the
          Request Quote button — our trade desk responds within 24 hours.
        </p>
      </div>

      <ol className="space-y-4">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.title} className="flex gap-4" data-testid={`procedure-step-${i + 1}`}>
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-4 w-4 text-primary/70" />
                  <h3 className="font-medium text-foreground">{s.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground bg-background rounded-lg p-3 border border-border/60">
        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <p>
          All transactions are subject to KYC, end-user certification, and applicable
          sanctions screening. Christ Kingdom Marketplace acts as the trade facilitator;
          final price is fixed at contract signing based on prevailing market rates.
        </p>
      </div>
    </section>
  );
}
